import { useState, useEffect } from 'react';
import { Bug, X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    function onExternal() { setOpen(true); }
    window.addEventListener('emina:open-bug-report', onExternal);
    return () => window.removeEventListener('emina:open-bug-report', onExternal);
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (description.trim().length < 5) { setError('Opis je prekratak.'); return; }
    setSending(true); setError(null);
    try {
      const r = await fetch(`${API}/public/bug-reports`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type, description: description.trim(), email: email.trim() || null,
          url: typeof window !== 'undefined' ? window.location.href : null,
          browser_os: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        })
      });
      const d = await r.json();
      if (d.ok) { setDone(true); setDescription(''); setEmail(''); }
      else setError(d.error || 'Greška. Pokušajte ponovo.');
    } catch { setError('Mreža nedostupna. Pokušajte ponovo.'); }
    finally { setSending(false); }
  }

  function close() {
    setOpen(false);
    setTimeout(() => { setDone(false); setError(null); }, 250);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Prijavi grešku"
        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
        data-testid="bug-report-toggle"
      >
        <Bug size={18} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="bug-report-modal">
          <div className="bg-white dark:bg-[#0f172a] rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-200 dark:border-gray-800" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Bug size={18} className="text-red-600" /> Prijavi grešku</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pomozite nam poboljšati portal. Vaša prijava šalje se timu.</p>
              </div>
              <button onClick={close} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" data-testid="bug-report-close"><X size={20} /></button>
            </div>

            {done ? (
              <div className="py-8 text-center">
                <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Hvala! Vaša prijava je zaprimljena.</p>
                <button onClick={close} className="mt-4 text-xs text-gray-500 hover:text-red-600 dark:text-gray-400">Zatvori</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Vrsta</label>
                  <div className="flex gap-2">
                    {[['bug','Greška'], ['feedback','Povratna informacija'], ['idea','Ideja']].map(([v, l]) => (
                      <button type="button" key={v} onClick={() => setType(v)}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${type === v ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-[#0b1220] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-red-400'}`}
                        data-testid={`bug-type-${v}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Opis <span className="text-red-500">*</span></label>
                  <textarea
                    required minLength={5} rows={4}
                    value={description} onChange={e => setDescription(e.target.value)}
                    placeholder="Što se dogodilo? Koraci za reprodukciju?"
                    className="w-full bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-red-400"
                    data-testid="bug-description"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail (nije obavezno)</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="vas@email.com"
                    className="w-full bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-red-400"
                    data-testid="bug-email"
                  />
                </div>
                {error && <p className="text-xs text-red-600" data-testid="bug-error">{error}</p>}
                <button type="submit" disabled={sending}
                  className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="bug-submit">
                  <Send size={14} /> {sending ? 'Šaljem…' : 'Pošalji'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
